(function (scope) {
  function eligible(item) {
    return !item.selling_plan_allocation && Object.keys(item.properties || {}).length === 0;
  }

  function indexBundles(config) {
    const bundles = new Map();
    const pairs = new Map();
    const shirts = new Set();
    const pants = new Set();
    let mug;
    let tumbler;
    for (const bundle of config.bundles) {
      const shirt = bundle.components.find(c => c.handle === 'pep-dude-wheres-my-coffee-shirt');
      const pant = bundle.components.find(c => c.handle === 'sff-pajama-pants');
      const cup = bundle.components.find(c => c.handle === 'pep-dude-wheres-my-coffee-mug');
      const travel = bundle.components.find(c => c.handle === 'pep-dude-wheres-my-coffee-tumbler');
      if (!shirt || !pant || bundle.components.some(c => c.quantity !== 1)) throw new Error('Invalid Pep bundle configuration');
      const tier = (cup ? 1 : 0) + (travel ? 2 : 0);
      const pair = shirt.id + ':' + pant.id;
      if (!pairs.has(pair)) pairs.set(pair, {});
      pairs.get(pair)[tier] = bundle;
      bundles.set(String(bundle.id), { ...bundle, pair, tier });
      shirts.add(String(shirt.id));
      pants.add(String(pant.id));
      if (cup) mug = String(cup.id);
      if (travel) tumbler = String(travel.id);
    }
    return { bundles, pairs, shirts, pants, mug, tumbler };
  }

  function fingerprint(cart) {
    return JSON.stringify({ token: cart.token, items: cart.items.map(i => [i.key, i.quantity]), note: cart.note });
  }

  function contents(cart, config) {
    const index = indexBundles(config);
    const counts = new Map();
    for (const item of cart.items) {
      const bundle = index.bundles.get(String(item.variant_id || item.id));
      const properties = JSON.stringify(Object.entries(item.properties || {}).sort());
      const plan = item.selling_plan_allocation?.selling_plan?.id || '';
      const components = bundle ? bundle.components : [{ id: String(item.variant_id || item.id), quantity: 1 }];
      for (const component of components) {
        const key = component.id + '|' + properties + '|' + plan;
        counts.set(key, (counts.get(key) || 0) + component.quantity * item.quantity);
      }
    }
    return JSON.stringify([...counts.entries()].sort(([a], [b]) => a.localeCompare(b)));
  }

  function plan(cart, config, now = Date.now()) {
    if (now >= Date.parse(config.endsAt)) return null;
    const index = indexBundles(config);
    const items = cart.items.filter(eligible).map(item => ({ ...item, id: String(item.variant_id || item.id), remaining: item.quantity }));
    const loose = id => items.filter(item => item.id === String(id) && !index.bundles.has(item.id));
    const count = id => loose(id).reduce((n, item) => n + item.remaining, 0);
    const added = new Map();
    let setCount = 0;
    function consume(id, amount) {
      for (const item of loose(id)) {
        const taken = Math.min(item.remaining, amount);
        item.remaining -= taken;
        amount -= taken;
        if (!amount) break;
      }
      if (amount) throw new Error('Pep cart quantities changed');
    }
    function add(bundle, quantity) {
      if (!quantity) return;
      added.set(String(bundle.id), (added.get(String(bundle.id)) || 0) + quantity);
      setCount += quantity;
    }
    for (const item of items) {
      const bundle = index.bundles.get(item.id);
      if (!bundle || bundle.tier === 3) continue;
      const tiers = index.pairs.get(bundle.pair);
      if (bundle.tier === 0) {
        const full = Math.min(item.remaining, count(index.mug), count(index.tumbler));
        if (full) {
          consume(index.mug, full);
          consume(index.tumbler, full);
          item.remaining -= full;
          add(tiers[3], full);
        }
        for (const [tier, id] of [[1, index.mug], [2, index.tumbler]]) {
          const quantity = Math.min(item.remaining, count(id));
          if (!quantity) continue;
          consume(id, quantity);
          item.remaining -= quantity;
          add(tiers[tier], quantity);
        }
      } else {
        const missing = bundle.tier === 1 ? index.tumbler : index.mug;
        const quantity = Math.min(item.remaining, count(missing));
        if (!quantity) continue;
        consume(missing, quantity);
        item.remaining -= quantity;
        add(tiers[3], quantity);
      }
    }
    for (const shirt of items.filter(item => index.shirts.has(item.id))) {
      for (const pant of items.filter(item => index.pants.has(item.id))) {
        const tiers = index.pairs.get(shirt.id + ':' + pant.id);
        if (!tiers) continue;
        let quantity = Math.min(shirt.remaining, pant.remaining);
        if (!quantity) continue;
        shirt.remaining -= quantity;
        pant.remaining -= quantity;
        const full = Math.min(quantity, count(index.mug), count(index.tumbler));
        if (full) {
          consume(index.mug, full);
          consume(index.tumbler, full);
          add(tiers[3], full);
          quantity -= full;
        }
        for (const [tier, id] of [[1, index.mug], [2, index.tumbler]]) {
          const withExtra = Math.min(quantity, count(id));
          if (!withExtra) continue;
          consume(id, withExtra);
          add(tiers[tier], withExtra);
          quantity -= withExtra;
        }
        add(tiers[0], quantity);
      }
    }
    if (!added.size) return null;
    const updates = {};
    for (const [id, quantity] of added) {
      const target = items.find(item => item.id === id);
      if (target) target.remaining += quantity;
      else updates[id] = quantity;
    }
    for (const item of items) if (item.remaining !== item.quantity) updates[item.key] = item.remaining;
    return { updates, setCount };
  }

  const engine = { plan, contents, fingerprint };
  if (typeof module !== 'undefined' && module.exports) module.exports = engine;
  scope.PepCartEngine = engine;
})(typeof window !== 'undefined' ? window : globalThis);
